import { useEffect, useState, useMemo } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { useNavigate, useLocation } from "react-router-dom";

/* ─── Petal Spinner (matches PearsonVUE) ─── */
const PetalSpinner = () => (
  <svg width="64" height="64" viewBox="0 0 64 64">
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = i * 30;
      const delay = -(1 - i / 12);
      return (
        <rect key={i} x="29" y="4" width="6" height="16" rx="3" fill="rgb(50,80,160)"
          transform={`rotate(${angle} 32 32)`} opacity="0.15">
          <animate attributeName="opacity" values="1;0.15" dur="1s" begin={`${delay}s`} repeatCount="indefinite" />
        </rect>
      );
    })}
  </svg>
);

const hoverBlueStyle = `
  .pvue-btn { color: white; background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; padding: 0 10px; font-family: Tahoma, sans-serif; font-size: 14pt; transition: color 0.12s; height: 100%; white-space: nowrap; }
  .pvue-btn:hover, .pvue-btn:hover * { color: #60a5fa !important; }
  .pvue-btn-underbar { color: white; background: none; border: none; cursor: pointer; display: inline-flex; align-items: center; gap: 4px; padding: 0 8px; font-family: Tahoma, sans-serif; font-size: 12pt; transition: color 0.12s; height: 100%; }
  .pvue-btn-underbar:hover, .pvue-btn-underbar:hover * { color: #60a5fa !important; }
  .pvue-nav-left  { border-right: 2px solid #FFFFFF; height: 100%; display: inline-flex; align-items: center; }
  .pvue-nav-right { border-left:  2px solid #FFFFFF; height: 100%; display: inline-flex; align-items: center; }
  .pvue-ic { background-image: url('/default.png'); background-repeat: no-repeat; }
  .pvue-ic-end-exam       { background-position: 0 -1188px; padding-left: 28px !important; }
  .pvue-ic-end-exam:hover { background-position: 0 -1231px; }
  .pvue-ic-rev-screen       { background-position: 0 -1274px; padding-left: 25px !important; }
  .pvue-ic-rev-screen:hover { background-position: 0 -1317px; }
  .pvue-ic-previous       { background-position: 0 -1618px; padding-left: 22px !important; }
  .pvue-ic-previous:hover { background-position: 0 -1660px; }
  .pvue-ic-navigator       { background-position: 0 -1702px; padding-left: 22px !important; }
  .pvue-ic-navigator:hover { background-position: 0 -1745px; }
  .pvue-ic-next       { background-position: right -1828px; padding-right: 22px !important; }
  .pvue-ic-next:hover { background-position: right -1869px; }
  .pvue-ic-flag       { background-position: 0 -970px;  padding-left: 26px !important; }
  .pvue-ic-flag:hover { background-position: 0 -1015px; }
  .pvue-ic-flag-sel   { background-position: 0 -1060px; padding-left: 26px !important; }
  .nav-scroll::-webkit-scrollbar { width: 12px; }
  .nav-scroll::-webkit-scrollbar-track { background: #e0e0e0; }
  .nav-scroll::-webkit-scrollbar-thumb { background: #8791ab; border-radius: 2px; }
`;

const ExamPageClassic = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { year, paper, examType, includeOfficial, includeCustom, includeCommunity, includeContingency, numQuestions, topics, communityProvider, communityPaper } = location.state || {};

  const examDuration = examType === "mat" ? 60 * 60 : 75 * 60;

  const [allQuestions, setAllQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(examDuration);
  const [showNavigator, setShowNavigator] = useState(false);
  const [flaggedQuestions, setFlaggedQuestions] = useState({});
  const [visited, setVisited] = useState(new Set());
  const [showBuffer, setShowBuffer] = useState(false);
  const [showEndExamModal, setShowEndExamModal] = useState(false);
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const [currentPaper, setCurrentPaper] = useState(1);
  const [showReview, setShowReview] = useState(false);
  const [reviewUnlocked, setReviewUnlocked] = useState(false);
  const [reviewTab, setReviewTab] = useState("all");
  const [showPaper2Confirm, setShowPaper2Confirm] = useState(false);
  const [paper2Started, setPaper2Started] = useState(false); // eslint-disable-line no-unused-vars
  const [paper1Answers, setPaper1Answers] = useState({});

  const isCommunityFullMock = communityProvider !== undefined && communityPaper === undefined;
  const isFullMock = (year !== undefined && paper === undefined) || isCommunityFullMock;

  const paper1Questions = useMemo(() => allQuestions.filter(q => isCommunityFullMock ? q.communityPaper === 1 : q.paper === 1), [allQuestions, isCommunityFullMock]);
  const paper2Questions = useMemo(() => allQuestions.filter(q => isCommunityFullMock ? q.communityPaper === 2 : q.paper === 2), [allQuestions, isCommunityFullMock]);
  const questions = useMemo(() => {
    if (!isFullMock) return allQuestions;
    return currentPaper === 1 ? paper1Questions : paper2Questions;
  }, [isFullMock, currentPaper, allQuestions, paper1Questions, paper2Questions]);

  useEffect(() => {
    const fetchQuestions = async () => {
      const qSnap = await getDocs(collection(db, "questions"));
      let qData = [];
      qSnap.forEach((doc) => {
        const data = { id: doc.id, ...doc.data() };
        let include = true;
        if (communityProvider !== undefined) {
          if (communityPaper !== undefined) {
            include = data.communityProvider === communityProvider && data.communityPaper === communityPaper;
          } else {
            include = data.communityProvider === communityProvider;
          }
        } else {
          if (year !== undefined) include = include && data.year === parseInt(year);
          if (paper !== undefined) {
            const paperVal = isNaN(parseInt(paper)) ? paper : parseInt(paper);
            include = include && data.paper === paperVal;
          }
          if (includeOfficial !== undefined || includeCustom !== undefined || includeCommunity !== undefined || includeContingency !== undefined) {
            const shouldIncludeOfficial = includeOfficial !== false;
            const shouldIncludeCustom = includeCustom !== false;
            if (data.questionType === "official") include = include && shouldIncludeOfficial;
            else if (data.questionType === "custom") include = include && shouldIncludeCustom;
            else if (data.questionType === "community") include = include && (includeCommunity === true);
            else if (data.questionType === "contingency") include = include && (includeContingency === true);
          }
          if (topics && topics.length > 0 && !topics.includes("all")) include = include && topics.includes(data.topic);
        }
        if (include) qData.push(data);
      });
      if (communityProvider !== undefined || year !== undefined) {
        qData.sort((a, b) => {
          const pa = typeof a.paper === "number" ? a.paper : 0;
          const pb = typeof b.paper === "number" ? b.paper : 0;
          if (pa !== pb) return pa - pb;
          return (a.questionNumber ?? 0) - (b.questionNumber ?? 0);
        });
      } else {
        qData = qData.sort(() => Math.random() - 0.5);
      }
      if (numQuestions) setAllQuestions(qData.slice(0, numQuestions));
      else setAllQuestions(qData);
    };
    fetchQuestions();
  }, [year, paper, includeOfficial, includeCustom, includeCommunity, includeContingency, numQuestions, topics, communityProvider, communityPaper]);

  useEffect(() => {
    if (showPaper2Confirm || showBuffer) return;
    const timer = setInterval(() => setTimeLeft((t) => Math.max(t - 1, 0)), 1000);
    return () => clearInterval(timer);
  }, [showPaper2Confirm, showBuffer]);

  useEffect(() => {
    if (!showReview) setVisited((prev) => new Set(prev).add(current));
  }, [current, showReview]);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const handleAnswer = (val) => setAnswers({ ...answers, [current]: val });
  const toggleFlag = () => setFlaggedQuestions({ ...flaggedQuestions, [current]: !flaggedQuestions[current] });

  const getQuestionStatus = (index) => {
    if (answers[index] !== undefined) return "Complete";
    if (visited.has(index)) return "Incomplete";
    return "Unseen";
  };

  const calculateScore = () => {
    let correct = 0;
    const allAnswers = isFullMock ? { ...paper1Answers, ...answers } : answers;
    const allQs = isFullMock ? [...paper1Questions, ...paper2Questions] : questions;
    allQs.forEach((q, index) => {
      const answerKey = isFullMock && index >= paper1Questions.length ? index - paper1Questions.length : index;
      const ans = isFullMock && index >= paper1Questions.length
        ? answers[answerKey]
        : (isFullMock ? paper1Answers[answerKey] : allAnswers[index]);
      if (ans !== undefined) {
        const userAnswerIndex = q.options.indexOf(ans);
        if (q.correct === userAnswerIndex) correct++;
      }
    });
    return correct;
  };

  const calculateResults = () => {
    const totalTimeTaken = examDuration - timeLeft;
    const allAnswers = isFullMock ? { ...paper1Answers, ...answers } : answers;
    const answeredQuestions = Object.keys(allAnswers).length;
    const totalQs = isFullMock ? paper1Questions.length + paper2Questions.length : questions.length;
    const avgTimePerQuestion = answeredQuestions > 0 ? totalTimeTaken / answeredQuestions : 0;
    const score = calculateScore();
    return {
      score, total: totalQs, questionsAnswered: answeredQuestions,
      avgTime: `${Math.floor(avgTimePerQuestion)}s`,
      totalTime: `${Math.floor(totalTimeTaken / 60)}m`,
    };
  };

  useEffect(() => {
    if (showResults) {
      const results = calculateResults();
      // Save session to localStorage for Performance tab
      const paperLabel = year !== undefined
        ? (paper === "section-a" ? `MAT ${year} Section A` : paper !== undefined ? `TMUA ${year} Paper ${paper}` : `TMUA ${year}`)
        : communityProvider !== undefined
          ? (communityPaper !== undefined ? `${communityProvider} Paper ${communityPaper}` : `${communityProvider} Mock`)
          : "Custom Practice";
      const allQs = isFullMock ? [...paper1Questions, ...paper2Questions] : questions;
      const topicResults = {};
      allQs.forEach((q, index) => {
        const topic = q.topic || "Unknown";
        if (!topicResults[topic]) topicResults[topic] = { correct: 0, answered: 0 };
        const ans = isFullMock && index >= paper1Questions.length
          ? answers[index - paper1Questions.length]
          : (isFullMock ? paper1Answers[index] : answers[index]);
        if (ans !== undefined) {
          topicResults[topic].answered++;
          if (q.correct === q.options.indexOf(ans)) topicResults[topic].correct++;
        }
      });
      const session = {
        id: Date.now().toString(),
        date: new Date().toISOString().split("T")[0],
        paper: paperLabel,
        score: results.score,
        total: results.total,
        answered: results.questionsAnswered,
        pct: results.total > 0 ? Math.round(results.score / results.total * 100) : 0,
        grade: results.total > 0 ? (results.score / results.total * 9).toFixed(1) : "0.0",
        avgTimeSecs: parseInt(results.avgTime) || 0,
        totalTimeMins: parseInt(results.totalTime) || 0,
        timed: true,
        topicResults,
      };
      const existing = JSON.parse(localStorage.getItem("tmuaprep_sessions") || "[]");
      existing.unshift(session);
      localStorage.setItem("tmuaprep_sessions", JSON.stringify(existing.slice(0, 100)));
      navigate("/exam-results", { state: results });
    }
  }, [showResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const unseenCount = useMemo(() => questions.filter((_, i) => getQuestionStatus(i) === "Unseen").length, [questions, answers, visited]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const incompleteCount = useMemo(() => questions.filter((_, i) => getQuestionStatus(i) === "Incomplete").length, [questions, answers, visited]);

  const filteredReviewQuestions = useMemo(() => {
    return questions.map((q, i) => ({ ...q, _idx: i, _status: getQuestionStatus(i), _flagged: !!flaggedQuestions[i] }))
      .filter(q => {
        if (reviewTab === "unseen") return q._status === "Unseen";
        if (reviewTab === "incomplete") return q._status === "Incomplete";
        return true;
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, reviewTab, answers, visited, flaggedQuestions]);

  const handleNext = () => {
    if (current === questions.length - 1) {
      if (isFullMock && currentPaper === 1) {
        setReviewUnlocked(true);
        setShowReview(true);
      } else {
        setShowScoreModal(true);
      }
    } else {
      setShowBuffer(true);
      setTimeout(() => {
        setShowBuffer(false);
        setCurrent((prev) => Math.min(prev + 1, questions.length - 1));
      }, 350);
    }
  };

  const handlePrevious = () => setCurrent((prev) => Math.max(prev - 1, 0));

  const startPaper2 = () => {
    setShowPaper2Confirm(false);
    setShowBuffer(true);
    setPaper1Answers({ ...answers });
    setTimeout(() => {
      setShowBuffer(false);
      setCurrentPaper(2);
      setPaper2Started(true);
      setAnswers({});
      setFlaggedQuestions({});
      setVisited(new Set());
      setCurrent(0);
      setTimeLeft(examDuration);
      setShowReview(false);
    }, 2500);
  };

  const renderMathHtml = (text) => {
    return text
      .replace(/\\begin\{enumerate\}[\s\S]*?\\end\{enumerate\}/g, (match) => {
        const contentMatch = match.match(/\\begin\{enumerate\}(?:\[.*?\])?([\s\S]*?)\\end\{enumerate\}/);
        if (!contentMatch) return "";
        const content = contentMatch[1];
        const items = [];
        const itemRegex = /\\item\s+([\s\S]*?)(?=\\item|$)/g;
        let itemMatch;
        while ((itemMatch = itemRegex.exec(content)) !== null) items.push(itemMatch[1].trim());
        if (items.length > 0) {
          const roman = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];
          return items.map((item, idx) => `${roman[idx]}. ${item}`).join("<br/><br/>");
        }
        return "";
      })
      .replace(/\$\$([\s\S]*?)\$\$/g, "\\[$1\\]")
      .replace(/\$([^$]+)\$/g, "\\($1\\)")
      .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
      .replace(/\\textbf\{([^}]+)\}/g, "<strong>$1</strong>")
      .replace(/\\textit\{([^}]+)\}/g, "<em>$1</em>")
      .replace(/\\dfrac/g, "\\frac")
      .replace(/\n/g, "<br/>");
  };

  /* ─── REVIEW SCREEN ─── */
  if (showReview && !showBuffer) {
    return (
      <div className="min-h-screen flex flex-col" style={{ fontFamily: "Tahoma, sans-serif", backgroundColor: "#fff", color: "black" }}>
        <style>{hoverBlueStyle}</style>
        <div className="flex justify-between items-center text-white px-4" style={{ backgroundColor: "rgb(0,109,170)", height: "45px" }}>
          <span style={{ fontSize: "16pt" }}>{examType === "mat" ? "Mathematics Admissions Test" : "Test of Mathematics for University Admission"}</span>
          <span style={{ fontSize: "12pt" }}>Time Remaining {formatTime(timeLeft)}</span>
        </div>
        <div className="flex justify-between items-center px-3 text-white" style={{ backgroundColor: "rgb(70,120,190)", height: "30px" }}>
          <button className="pvue-btn-underbar">Instructions</button>
          <select className="text-white bg-transparent border-none outline-none px-2" style={{ fontFamily: "Tahoma, sans-serif", fontSize: "12pt" }}>
            <option>Color Scheme</option>
          </select>
        </div>
        <div className="flex-1" style={{ backgroundColor: "rgb(242,243,245)", overflowY: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px 16px" }}>
            <h3 style={{ fontFamily: "Tahoma, sans-serif", fontSize: "20pt", fontWeight: "bold", margin: 0 }}>Review</h3>
            <button onClick={() => { setShowReview(false); setCurrent(0); }}
              style={{ fontFamily: "Tahoma, sans-serif", fontSize: "12pt", backgroundColor: "rgb(0,109,170)", color: "white", border: "none", padding: "6px 18px", cursor: "pointer" }}>
              Review <span style={{ textDecoration: "underline" }}>A</span>ll
            </button>
          </div>
          <div style={{ display: "flex", borderBottom: "1px solid #C0C0C0" }}>
            {[{ key: "all", label: "All", count: questions.length }, { key: "incomplete", label: "Incomplete", count: incompleteCount }, { key: "unseen", label: "Unseen", count: unseenCount }].map(({ key, label, count }) => (
              <button key={key} onClick={() => setReviewTab(key)}
                style={{ flex: 1, fontFamily: "Arial, sans-serif", fontSize: "12pt", background: "none", border: "none", borderBottom: reviewTab === key ? "3px solid rgb(0,109,170)" : "3px solid transparent", marginBottom: "-1px", color: reviewTab === key ? "rgb(0,109,170)" : "#636d87", padding: "8px 16px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px" }}>
                {label} ({count})
              </button>
            ))}
          </div>
          <div style={{ backgroundColor: "white" }}>
            <table cellPadding="0" cellSpacing="0" border="0" style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  {["Question", "Title", "Status", "Flagged", ""].map((h, i) => (
                    <th key={i} style={{ padding: "10px 16px", textAlign: "left", fontFamily: "sans-serif", fontSize: "9pt", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.04em", color: "#525b73", borderBottom: "1px solid #C0C0C0" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredReviewQuestions.map((q) => (
                  <tr key={q._idx} style={{ borderBottom: "1px solid #E8E8E8" }}>
                    <th style={{ padding: "10px 16px", fontWeight: "normal", fontFamily: "sans-serif", fontSize: "12pt", textAlign: "left" }}>{q._idx + 1}</th>
                    <td style={{ padding: "10px 16px", fontFamily: "sans-serif", fontSize: "12pt" }}>Question</td>
                    <td style={{ padding: "10px 16px" }}>
                      {q._status === "Complete" ? <span style={{ fontFamily: "sans-serif", fontSize: "12pt" }}>Complete</span> : (
                        <span style={{ display: "inline-block", fontFamily: "sans-serif", fontSize: "11pt", fontWeight: "600", color: q._status === "Unseen" ? "#92400e" : "#D90000", backgroundColor: q._status === "Unseen" ? "#fef3c7" : "#fee2e2", padding: "2px 10px", borderRadius: "9999px" }}>{q._status}</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 16px", fontFamily: "sans-serif", fontSize: "12pt" }}>{q._flagged ? "Yes" : "No"}</td>
                    <td style={{ padding: "10px 16px" }}>
                      <button onClick={() => { setCurrent(q._idx); setShowReview(false); }}
                        style={{ fontFamily: "Tahoma, sans-serif", fontSize: "12pt", color: "rgb(0,109,170)", background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: "bold" }}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center text-white" style={{ height: "33px", backgroundColor: "rgb(0,109,170)", borderTop: "2px solid white" }}>
          <div className="pvue-nav-left" style={{ paddingLeft: "5px" }}>
            <button className="pvue-btn pvue-ic pvue-ic-end-exam" onClick={() => setShowPaper2Confirm(true)}><span><span style={{ textDecoration: "underline" }}>E</span>nd Review</span></button>
          </div>
        </div>
        {showPaper2Confirm && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[3000]" onClick={() => setShowPaper2Confirm(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "380px", backgroundColor: "rgb(0,109,170)", color: "white", fontFamily: "Tahoma, Arial, Helvetica, sans-serif" }}>
              {/* Title */}
              <div style={{ padding: "8px 12px", fontSize: "12pt", fontWeight: "normal", borderBottom: "1px solid rgba(255,255,255,0.4)" }}>Proceed to Paper 2</div>
              {/* Body */}
              <div style={{ padding: "16px 12px 12px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                <div style={{ flexShrink: 0, width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "rgb(20,60,120)", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "bold", color: "white", fontStyle: "italic", marginTop: "1px" }}>i</div>
                <div style={{ fontSize: "12pt", fontWeight: "normal", lineHeight: "1.5" }}>
                  <div>You are about to begin <strong>Paper 2</strong>.</div>
                  <div style={{ marginTop: "6px" }}>You will have <strong>75 minutes</strong> for Paper 2.</div>
                  <div style={{ marginTop: "6px", opacity: 0.85 }}>You will not be able to return to Paper 1 questions.</div>
                </div>
              </div>
              {/* Button pane */}
              <div style={{ padding: "8px 12px 12px", display: "flex", justifyContent: "center", gap: "12px" }}>
                <button onClick={startPaper2} style={{ backgroundColor: "rgb(0,109,170)", color: "white", border: "1px solid white", padding: "3px 16px", fontSize: "12pt", fontFamily: "Tahoma, Arial, Helvetica, sans-serif", cursor: "pointer" }}>Begin Paper 2</button>
                <button onClick={() => setShowPaper2Confirm(false)} style={{ backgroundColor: "rgb(0,109,170)", color: "white", border: "1px solid white", padding: "3px 16px", fontSize: "12pt", fontFamily: "Tahoma, Arial, Helvetica, sans-serif", cursor: "pointer" }}>Go Back</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ─── BUFFER ─── */
  if (showBuffer) {
    return (
      <div className="min-h-screen flex flex-col" style={{ fontFamily: "Tahoma, sans-serif" }}>
        <div className="flex items-center text-white px-4" style={{ backgroundColor: "rgb(0,109,170)", height: "45px" }}>
          <span style={{ fontSize: "16pt" }}>{examType === "mat" ? "Mathematics Admissions Test" : "Test of Mathematics for University Admission"}</span>
        </div>
        <div style={{ backgroundColor: "rgb(70,120,190)", height: "30px" }} />
        <div className="flex-1 flex items-center justify-center" style={{ backgroundColor: "rgb(200,200,200)" }}>
          <PetalSpinner />
        </div>
        <div style={{ height: "33px", backgroundColor: "rgb(0,109,170)", borderTop: "1px solid rgba(255,255,255,0.4)" }} />
      </div>
    );
  }

  /* ─── MAIN EXAM ─── */
  return (
    <MathJaxContext>
      <div className="min-h-screen flex flex-col" style={{ fontFamily: "Tahoma, sans-serif", backgroundColor: "rgb(255,255,255)", color: "black", fontSize: "15px", lineHeight: "1.6" }}>
        <style>{hoverBlueStyle}</style>
        <div className="flex justify-between items-center text-white px-4" style={{ backgroundColor: "rgb(0,109,170)", height: "45px" }}>
          <span style={{ fontSize: "16pt" }}>{examType === "mat" ? "Mathematics Admissions Test" : "Test of Mathematics for University Admission"}</span>
          <div className="flex flex-col items-end" style={{ fontSize: "12pt", lineHeight: "1.3" }}>
            <span>Time Remaining {formatTime(timeLeft)}</span>
            <span>{current + 1} of {questions.length || 20}</span>
          </div>
        </div>
        <div className="flex justify-end items-center px-3 space-x-4 text-white" style={{ backgroundColor: "rgb(70,120,190)", height: "30px" }}>
          <button onClick={toggleFlag} className={`pvue-btn-underbar pvue-ic ${flaggedQuestions[current] ? "pvue-ic-flag-sel" : "pvue-ic-flag"}`}>
            <span><span style={{ textDecoration: "underline" }}>F</span>lag for Review</span>
          </button>
          <select className="text-white bg-transparent border-none outline-none px-2" style={{ fontFamily: "Tahoma, sans-serif", fontSize: "12pt" }}>
            <option>Color Scheme</option>
          </select>
        </div>
        <div className="flex-1 px-8 py-8">
          {questions.length > 0 && (
            <>
              <div className="mb-8">
                <MathJax dynamic>
                  <div style={{ fontSize: "12pt", fontFamily: "Tahoma, sans-serif", lineHeight: "1.6", whiteSpace: "pre-wrap", marginBottom: "20px" }}
                    dangerouslySetInnerHTML={{ __html: renderMathHtml(questions[current].question) }} />
                </MathJax>
                {questions[current].diagramUrl && (
                  <div style={{ marginTop: "16px" }}>
                    <img
                      src={questions[current].diagramUrl}
                      alt="Question diagram"
                      style={{ maxWidth: "100%", maxHeight: `${360 * (questions[current].diagramScale ?? 1)}px`, border: "1px solid #ced5e4" }}
                    />
                  </div>
                )}
              </div>
              <div style={{ height: "20px" }} />
              <div className="space-y-5">
                {questions[current].options.map((opt, i) => (
                  <label key={i} className="flex items-center space-x-2 cursor-pointer">
                    <input type="radio" name={`q-${current}`} checked={answers[current] === opt} onChange={() => handleAnswer(opt)} />
                    <MathJax dynamic><span dangerouslySetInnerHTML={{ __html: renderMathHtml(opt) }} /></MathJax>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="flex justify-between items-center text-white" style={{ height: "33px", backgroundColor: "rgb(0,109,170)", borderTop: "2px solid white" }}>
          <div className="flex items-center h-full">
            <div className="pvue-nav-left" style={{ paddingLeft: "5px" }}>
              <button className="pvue-btn pvue-ic pvue-ic-end-exam" onClick={() => setShowEndExamModal(true)}>
                <span><span style={{ textDecoration: "underline" }}>E</span>nd Exam</span>
              </button>
            </div>
            {isFullMock && currentPaper === 1 && reviewUnlocked && (
              <div className="pvue-nav-left" style={{ paddingLeft: "5px" }}>
                <button className="pvue-btn pvue-ic pvue-ic-rev-screen" onClick={() => setShowReview(true)}>
                  <span>Review <span style={{ textDecoration: "underline" }}>S</span>creen</span>
                </button>
              </div>
            )}
          </div>
          <div className="flex items-center h-full">
            <div className="pvue-nav-right" style={{ paddingLeft: "5px" }}>
              <button className="pvue-btn pvue-ic pvue-ic-previous" onClick={handlePrevious}>
                <span><span style={{ textDecoration: "underline" }}>P</span>revious</span>
              </button>
            </div>
            <div className="pvue-nav-right" style={{ paddingLeft: "12px" }}>
              <button className="pvue-btn pvue-ic pvue-ic-navigator" onClick={() => setShowNavigator(true)}>
                <span>Na<span style={{ textDecoration: "underline" }}>v</span>igator</span>
              </button>
            </div>
            <div className="pvue-nav-right">
              <button className="pvue-btn pvue-ic pvue-ic-next" onClick={handleNext}>
                <span><span style={{ textDecoration: "underline" }}>N</span>ext</span>
              </button>
            </div>
          </div>
        </div>

        {showNavigator && (
          <div className="fixed inset-0 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2147483257 }} onClick={() => setShowNavigator(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "660px", fontFamily: "Tahoma, Arial, Helvetica, sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,0.4)", backgroundColor: "rgb(0,109,170)" }}>
              {/* Title bar */}
              <div style={{ backgroundColor: "rgb(0,109,170)", color: "white", padding: "8px 14px", fontSize: "12pt" }}>
                Navigator<span> - select a question to go to it</span>
              </div>
              {/* Navigator body */}
              <div className="nav-scroll" style={{ backgroundColor: "white", maxHeight: "320px", overflowY: "auto" }}>
                <table cellSpacing="0" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ backgroundColor: "rgb(70,120,190)", color: "white", fontSize: "12pt" }}>
                    <tr>
                      <th style={{ padding: "7px 14px", textAlign: "left", fontWeight: "bold", borderRight: "1px solid #C0C0C0" }}>Question #</th>
                      <th style={{ padding: "7px 14px", textAlign: "left", fontWeight: "bold", borderRight: "1px solid #C0C0C0" }}>Status</th>
                      <th style={{ padding: "7px 14px", textAlign: "left", fontWeight: "bold" }}>Flagged for Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((_, index) => {
                      const status = getQuestionStatus(index);
                      const isCurrent = index === current;
                      const isFlagged = !!flaggedQuestions[index];
                      const rowBg = index % 2 === 0 ? "#F4F4F4" : "#FFFFFF";
                      return (
                        <tr key={index}
                          style={{ backgroundColor: isCurrent ? "#FFFD6F" : rowBg, cursor: "pointer", borderBottom: "1px solid #C0C0C0" }}
                          onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = "#FFFD6F"; }}
                          onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.backgroundColor = rowBg; }}
                          onClick={() => { setCurrent(index); setShowNavigator(false); }}>
                          <td style={{ padding: "5px 14px", fontSize: "12pt", borderRight: "1px solid #C0C0C0" }}>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a style={{ color: "inherit", textDecoration: "none" }}>Question {index + 1}</a>
                          </td>
                          <td style={{ padding: "5px 14px", fontSize: "12pt", color: status === "Complete" ? "black" : "#D90000", borderRight: "1px solid #C0C0C0" }}>{status}</td>
                          <td style={{ padding: "5px 14px", fontSize: "12pt" }}>
                            {isFlagged ? "Yes" : "\u00a0"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {/* Footer: count + Close button */}
              <div style={{ backgroundColor: "rgb(0,109,170)", color: "white", padding: "6px 14px", fontSize: "12pt", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>{questions.filter((_, i) => getQuestionStatus(i) !== "Complete").length} Unseen/Incomplete</span>
                <button onClick={() => setShowNavigator(false)}
                  style={{ border: "1px solid white", backgroundColor: "rgb(0,109,170)", color: "white", padding: "3px 16px", fontSize: "12pt", fontFamily: "Tahoma, Arial, Helvetica, sans-serif", cursor: "pointer" }}>
                  <span style={{ textDecoration: "underline" }}>C</span>lose
                </button>
              </div>
            </div>
          </div>
        )}

        {showScoreModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[3000]" onClick={() => setShowScoreModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "450px", border: "2px solid white" }}>
              <div style={{ backgroundColor: "rgb(0,109,170)", color: "white", fontWeight: "bold", padding: "6px 12px", borderBottom: "1px solid white" }}>Check Your Score</div>
              <div style={{ backgroundColor: "white", color: "black", padding: "20px", fontSize: "15px", textAlign: "center" }}>
                <div style={{ marginBottom: "12px" }}>You have reached the end of the exam.</div>
                <div style={{ marginBottom: "18px" }}>Would you like to check your score?</div>
                <div className="flex justify-center space-x-6">
                  <button onClick={() => { setShowScoreModal(false); setShowResults(true); }} style={{ backgroundColor: "white", color: "rgb(0,109,170)", border: "1px solid rgb(0,109,170)", padding: "4px 20px", fontSize: "15px", fontWeight: "bold", cursor: "pointer" }}>Yes</button>
                  <button onClick={() => setShowScoreModal(false)} style={{ backgroundColor: "white", color: "rgb(0,109,170)", border: "1px solid rgb(0,109,170)", padding: "4px 20px", fontSize: "15px", fontWeight: "bold", cursor: "pointer" }}>No</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showEndExamModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-[3000]" onClick={() => setShowEndExamModal(false)}>
            <div onClick={e => e.stopPropagation()} style={{ width: "380px", backgroundColor: "rgb(0,109,170)", color: "white", fontFamily: "Tahoma, Arial, Helvetica, sans-serif" }}>
              {/* Title */}
              <div style={{ padding: "8px 12px", fontSize: "12pt", fontWeight: "normal", borderBottom: "1px solid rgba(255,255,255,0.4)" }}>End Exam</div>
              {/* Body */}
              <div style={{ padding: "16px 12px 12px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
                {/* Info icon — sprite */}
                <div style={{ flexShrink: 0, width: "32px", height: "32px", borderRadius: "50%", backgroundColor: "rgb(20,60,120)", border: "2px solid white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: "bold", color: "white", fontStyle: "italic", marginTop: "1px" }}>i</div>
                <div style={{ fontSize: "12pt", fontWeight: "normal", lineHeight: "1.5" }}>
                  <div>You have chosen to end this exam.</div>
                  <div style={{ marginTop: "6px" }}>Are you sure you want to end this exam?</div>
                </div>
              </div>
              {/* Button pane */}
              <div style={{ padding: "8px 12px 12px", display: "flex", justifyContent: "center", gap: "12px" }}>
                <button onClick={() => navigate("/practice", { state: { selectedSubject: "tmua" } })} style={{ backgroundColor: "rgb(0,109,170)", color: "white", border: "1px solid white", padding: "3px 16px", fontSize: "12pt", fontFamily: "Tahoma, Arial, Helvetica, sans-serif", cursor: "pointer" }}><span style={{ textDecoration: "underline" }}>Y</span>es</button>
                <button onClick={() => setShowEndExamModal(false)} style={{ backgroundColor: "rgb(0,109,170)", color: "white", border: "1px solid white", padding: "3px 16px", fontSize: "12pt", fontFamily: "Tahoma, Arial, Helvetica, sans-serif", cursor: "pointer" }}><span style={{ textDecoration: "underline" }}>N</span>o</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MathJaxContext>
  );
};

export default ExamPageClassic;
